import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import { Box, Paper, Table, useTheme, TableContainer } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { getComparator, applySortFilter } from 'src/utils/filterData';

import TableHeaders from 'src/components/tableHeaders/TableHeaders';
import TablePaginations from 'src/components/table-pagination/TablePaginations';

import TagsTableBody from './tableBody';
import MenuPopover from '../popovers/MenuPopover';
import { TABLE_HEAD_TAGS } from './TagTableHeaders';
import AddUpdateTags from '../modals/AddUpdateTags';

function TagsTable({
  tags,
  selectedTags,
  setSelectedTags,
  isLoading,
  searchValue, // Received
  searchBy, // Received
  filterBy, // Received
  filterTitle, // Received
  handleFetchTags,
  departmentList,
  page,
  setPage,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { departmentIds, permissions } = useAuth();
  const { isSmallScreen } = useResponsiveScreen();
  const [currentPageTags, setCurrentPageTags] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [indeterminateCheckbox, setIndeterminateCheckbox] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const { showModal, hideModal } = useModal();

  const canUpdate = permissions?.includes('TAG:UPDATE');
  const canDelete = permissions?.includes('TAG:DELETE');
  const showOptionsColumn = canUpdate || canDelete;

  const hasScope = permissions?.includes('TICKET:SCOPE_DEPARTMENT');
  const canReadGlobal = !departmentIds || departmentIds.length === 0;
  const showDeptAndCreatorColumns = canReadGlobal || hasScope;

  const dynamicTableHeaders = useMemo(() => {
    const headers = showOptionsColumn
      ? TABLE_HEAD_TAGS
      : TABLE_HEAD_TAGS.filter((head) => head.id !== 'options');
    return headers;
  }, [showOptionsColumn]);

  const isSelected = (id) => selectedTags.indexOf(id) !== -1;

  const filteredTags = useMemo(() => {
    // 1. First: Apply free-text search query (ID, Name, Description, CreatedBy)
    let result = applySortFilter(
      tags,
      getComparator(order, orderBy),
      searchValue,
      searchBy
    );

    // 2. Second: Apply categorical department filter only (CreatedBy is now handled by text search above)
    if (filterBy && filterTitle) {
      const targetVal = filterTitle.toLowerCase().trim();
      result = result.filter((tag) => {
        if (filterBy === 'department') {
          return tag.department?.toLowerCase() === targetVal;
        }
        return true;
      });
    }

    return result;
  }, [searchValue, searchBy, filterBy, filterTitle, order, orderBy, tags]);

  useEffect(() => {
    // Update current page forms shareable when page, rowsPerPage, or formsShareable changes
    const startIdx = page * rowsPerPage;
    const endIdx = startIdx + rowsPerPage;
    setCurrentPageTags(filteredTags.slice(startIdx, endIdx));
  }, [page, rowsPerPage, filteredTags]);

  useEffect(() => {
    const newSelectdsId = currentPageTags.map((n) => n.id);
    if (newSelectdsId.every((id) => selectedTags.includes(id))) {
      setIndeterminateCheckbox(true);
    } else setIndeterminateCheckbox(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageTags, page, selectedTags]);

  const handleCloseMenu = () => {
    setPopoverAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page to 0 whenever rows per page changes
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = () => {
    const newSelecteds = currentPageTags.map((n) => n.id);
    if (newSelecteds.every((id) => selectedTags.includes(id))) {
      setSelectedTags(selectedTags.filter((id) => !newSelecteds.includes(id)));
      return;
    }
    setSelectedTags([...new Set([...selectedTags, ...newSelecteds])]);
  };

  const { isNotFound, searchIsNotFound } = useMemo(() => {
    const isNotFound = !filteredTags.length;
    const searchIsNotFound = !filteredTags.length && !!searchValue; // Binds to searchValue state
    return { isNotFound, searchIsNotFound };
  }, [filteredTags, searchValue]);

  const handleOpenMenu = (event, row) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedItem(row);
  };

  const handleOpenUpdateModal = (row) => {
    showModal({
      title: t('pages.tagsPage.updateTag'),
      Component: AddUpdateTags,
      props: {
        hideModal,
        handleFetchTags,
        row,
        departmentList,
        canUpdate,
      },
    });
  };

  return (
    <>
      <Box
        sx={{
          overflowX: 'auto',
          maxWidth: '100%',
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            // Styles the table scrollbar to be dynamic and dark-mode compliant
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(0, 0, 0, 0.15)',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          <Table size={isSmallScreen ? 'small' : 'medium'}>
            <TableHeaders
              order={order}
              orderBy={orderBy}
              headLabel={
                showDeptAndCreatorColumns
                  ? dynamicTableHeaders
                  : dynamicTableHeaders.filter(
                      (head) => !['department', 'createdBy'].includes(head.id)
                    )
              }
              rowCount={filteredTags.length}
              numSelected={selectedTags.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              showCheckBox={canDelete}
              indeterminate={indeterminateCheckbox}
            />

            <TagsTableBody
              isLoading={isLoading}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              filteredTags={filteredTags}
              page={page}
              rowsPerPage={rowsPerPage}
              isSelected={isSelected}
              setSelectedTags={setSelectedTags}
              handleOpenMenu={handleOpenMenu}
              handleOpenUpdateModal={handleOpenUpdateModal}
              filterName={searchValue} // Maps to searchValue
              canUpdate={canUpdate}
              canDelete={canDelete}
              showCheckBox={canDelete}
              showOptions={showOptionsColumn}
              showDeptAndCreator={showDeptAndCreatorColumns}
            />
          </Table>
        </TableContainer>
      </Box>

      {/* Table Pagination */}
      <TablePaginations
        filteredEntity={filteredTags}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />

      {/* menu popover */}
      <MenuPopover
        popoverAnchorEl={popoverAnchorEl}
        handleCloseMenu={handleCloseMenu}
        selectedItem={selectedItem}
        handleFetchTags={handleFetchTags}
        AddUpdateTags={AddUpdateTags}
        setSelectedTags={setSelectedTags}
        departmentList={departmentList}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />
    </>
  );
}

TagsTable.propTypes = {
  tags: PropTypes.array,
  selectedTags: PropTypes.array,
  setSelectedTags: PropTypes.func,
  isLoading: PropTypes.bool,
  searchValue: PropTypes.string,
  searchBy: PropTypes.string,
  filterBy: PropTypes.string,
  filterTitle: PropTypes.string,
  handleFetchTags: PropTypes.func,
  departmentList: PropTypes.array,
  page: PropTypes.number,
  setPage: PropTypes.func,
};

export default TagsTable;
